import { TrendingUp } from 'lucide-react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { defaultChartOptions, energyColors } from './charts/chartConfig';
import type { DailyOffGridPoint } from '@/types/energy';
import { formatDayLabel } from '@/lib/todayLine';

interface OffGridTrendChartProps {
  data: DailyOffGridPoint[];
}

export function OffGridTrendChart({ data }: OffGridTrendChartProps) {
  if (data.length === 0) {
    return (
      <Card className="relative">
        <div className="absolute top-1.5 right-2 z-10">
          <InfoTooltip
            content={
              <div className="space-y-1">
                <p className="font-semibold">Daily Off-Grid Share Trend</p>
                <p>Shows the daily percentage of energy that came from solar and battery sources over the selected time period.</p>
                <p className="text-muted-foreground">Track how energy independence fluctuates day-to-day. Higher percentages mean less reliance on grid and generators.</p>
              </div>
            }
          />
        </div>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5 text-[#8FB560]" />
            <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              Daily Off-Grid Share (%)
            </h3>
          </div>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center">
          <p className="text-muted-foreground">No trend data available</p>
        </CardContent>
      </Card>
    );
  }


  const options: ApexOptions = {
    ...defaultChartOptions,
    chart: {
      ...defaultChartOptions.chart,
      type: 'area',
      height: 200,
    },
    colors: [energyColors.solar],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    xaxis: {
      categories: data.map(d => formatDayLabel(d.date)),
      labels: {
        style: { colors: '#9ca3af', fontSize: '10px' },
        rotate: 0,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickAmount: Math.min(data.length - 1, 6),
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (val) => `${Math.round(val)}%`,
        style: { colors: '#9ca3af', fontSize: '10px' },
      },
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => `${Math.round(val)}%`,
      },
    },
    markers: {
      size: 0,
      hover: { size: 5 },
    },
  };

  const series = [{
    name: 'Off-Grid %',
    data: data.map(d => d.offGridPercent),
  }];

  return (
    <Card className="relative">
      <div className="absolute top-1.5 right-2 z-10">
        <InfoTooltip
          content={
            <div className="space-y-1">
              <p className="font-semibold">Daily Off-Grid Share Trend</p>
              <p>Shows the daily percentage of energy that came from solar and battery sources over the selected time period.</p>
              <p className="text-muted-foreground">Track how energy independence fluctuates day-to-day. Higher percentages mean less reliance on grid and generators.</p>
            </div>
          }
        />
      </div>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-1">
          <TrendingUp className="h-3.5 w-3.5 text-[#8FB560]" />
          <h3 className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Daily Off-Grid Share (%)
          </h3>
        </div>
      </CardHeader>
      <CardContent>
        <Chart
          type="area"
          height={200}
          options={options}
          series={series}
        />
      </CardContent>
    </Card>
  );
}
